import { useEffect, useSyncExternalStore } from "react";
import { Logger } from "@cede/utils";

const WS_URL = "wss://stream.crypto.com/exchange/v1/market";
const MAX_RECONNECT_ATTEMPTS = 10;
const MAX_RECONNECT_DELAY = 30_000;
const SETUP_DELAY = 1000; // Crypto.com recommends 1s delay after connection

// --- Module-level singleton managing a single shared WebSocket connection ---

type SetupFn = (ws: WebSocket) => (() => void) | void;
type MessageListener = (data: unknown) => void;
type ErrorListener = (error: string) => void;
type StatusListener = () => void;

interface Subscriber {
  setup: SetupFn;
  onMessage?: MessageListener;
  onError?: ErrorListener;
  cleanup: (() => void) | null;
}

let ws: WebSocket | null = null;
let isConnecting = false;
let isConnected = false;
let connectionError: string | null = null;
let reconnectAttempts = 0;
let reconnectTimeout: NodeJS.Timeout | undefined;
let setupTimeout: NodeJS.Timeout | undefined;
let disconnectTimeout: NodeJS.Timeout | undefined;

const subscribers = new Map<symbol, Subscriber>();
const statusListeners = new Set<StatusListener>();

// Notify React (via useSyncExternalStore) that connection state changed,
// triggering a snapshot comparison and re-render if isConnected or error differ.
function notifyStatusListeners() {
  statusListeners.forEach((listener) => listener());
}

function runSetup(subscriber: Subscriber) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  subscriber.cleanup?.();
  const cleanup = subscriber.setup(ws);
  subscriber.cleanup = cleanup ?? null;
}

function runAllSetups() {
  subscribers.forEach((subscriber) => runSetup(subscriber));
}

function cleanupAllSubscribers() {
  subscribers.forEach((subscriber) => {
    subscriber.cleanup?.();
    subscriber.cleanup = null;
  });
}

function disconnect() {
  cleanupAllSubscribers();

  if (disconnectTimeout) {
    clearTimeout(disconnectTimeout);
    disconnectTimeout = undefined;
  }

  if (setupTimeout) {
    clearTimeout(setupTimeout);
    setupTimeout = undefined;
  }

  if (ws) {
    ws.close();
    ws = null;
  }

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
  }

  isConnecting = false;
  isConnected = false;
  reconnectAttempts = 0;
  notifyStatusListeners();
}

function connect() {
  if (isConnecting || ws || subscribers.size === 0) return;

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = undefined;
  }

  isConnecting = true;
  connectionError = null;
  notifyStatusListeners();

  try {
    const socket = new WebSocket(WS_URL);

    socket.onopen = () => {
      isConnected = true;
      isConnecting = false;
      reconnectAttempts = 0;

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      notifyStatusListeners();

      // Crypto.com recommends 1s delay after connection before sending requests
      setupTimeout = setTimeout(() => {
        setupTimeout = undefined;
        if (socket.readyState === WebSocket.OPEN) {
          runAllSetups();
        }
      }, SETUP_DELAY);
    };

    socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        // Respond to heartbeat to keep connection alive
        if (msg.method === "public/heartbeat" && msg.id != null) {
          socket.send(JSON.stringify({ id: msg.id, method: "public/respond-heartbeat" }));
          return;
        }

        subscribers.forEach((subscriber) => {
          subscriber.onMessage?.(msg);
        });
      } catch {
        // Ignore parse errors for non-JSON messages
      }
    };

    socket.onerror = () => {
      if (socket !== ws) return;

      isConnecting = false;
      isConnected = false;
      const errorMsg = "WebSocket connection error";
      connectionError = errorMsg;
      notifyStatusListeners();
      subscribers.forEach((subscriber) => subscriber.onError?.(errorMsg));
    };

    socket.onclose = () => {
      // Ignore close events from stale sockets (e.g. after disconnect() already created a new one)
      if (socket !== ws) return;

      isConnecting = false;
      isConnected = false;
      ws = null;
      cleanupAllSubscribers();
      notifyStatusListeners();

      // Only reconnect if there are still active subscribers
      if (subscribers.size > 0 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY);
        reconnectAttempts++;

        Logger.log(
          `[cryptocomWS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
          Logger.types.INFO,
        );

        reconnectTimeout = setTimeout(() => {
          connect();
        }, delay);
      } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        const errorMsg = "Failed to reconnect after multiple attempts";
        Logger.error("[cryptocomWS] Max reconnection attempts reached, giving up");
        connectionError = errorMsg;
        notifyStatusListeners();
        subscribers.forEach((subscriber) => subscriber.onError?.(errorMsg));
      }
    };

    ws = socket;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Unknown error";
    Logger.error("[cryptocomWS] Failed to create WebSocket connection:", errorMessage, err);
    isConnecting = false;
    const errorMsg = "Failed to create WebSocket connection";
    connectionError = errorMsg;
    notifyStatusListeners();
    subscribers.forEach((subscriber) => subscriber.onError?.(errorMsg));
  }
}

function subscribe(subscriber: Subscriber): symbol {
  // Cancel any pending deferred disconnect
  if (disconnectTimeout) {
    clearTimeout(disconnectTimeout);
    disconnectTimeout = undefined;
  }

  const id = Symbol();
  subscribers.set(id, subscriber);

  if (isConnected && ws && !setupTimeout) {
    // Connection open and initial setup delay completed: run setup immediately
    runSetup(subscriber);
  } else if (subscribers.size === 1) {
    // First subscriber and no connection: open one
    connect();
  }

  return id;
}

function unsubscribe(id: symbol) {
  const subscriber = subscribers.get(id);
  if (subscriber) {
    subscriber.cleanup?.();
    subscriber.cleanup = null;
    subscribers.delete(id);
  }

  // Defer disconnect so React effect re-subscriptions can happen first
  // (React runs all cleanups before any new effects)
  if (subscribers.size === 0) {
    if (disconnectTimeout) clearTimeout(disconnectTimeout);
    disconnectTimeout = setTimeout(() => {
      disconnectTimeout = undefined;
      if (subscribers.size === 0) {
        disconnect();
      }
    }, 0);
  }
}

function getSnapshot() {
  return { isConnected, error: connectionError };
}

// Stable reference that only changes when the values change
let cachedSnapshot = getSnapshot();

function getSnapshotStable() {
  const next = getSnapshot();
  if (next.isConnected !== cachedSnapshot.isConnected || next.error !== cachedSnapshot.error) {
    cachedSnapshot = next;
  }
  return cachedSnapshot;
}

function subscribeToStatus(listener: StatusListener) {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

// Visibility handling (module-level, registered once)
let visibilityHandlerRegistered = false;

function registerVisibilityHandler() {
  if (visibilityHandlerRegistered || typeof window === "undefined") return;
  visibilityHandlerRegistered = true;

  window.addEventListener("visibilitychange", () => {
    if (subscribers.size === 0) return;

    if (document.visibilityState === "hidden") {
      disconnect();
    } else {
      connect();
    }
  });
}

// --- React hook wrapping the singleton ---

interface UseCryptocomWsConnectionProps {
  /** Called when the WS is open (or on reconnect). Should return a cleanup/unsubscribe function. */
  setup: SetupFn;
  /** Called for every incoming WS message (after heartbeat handling). */
  onMessage?: MessageListener;
  enabled?: boolean;
  onError?: ErrorListener;
}

interface UseCryptocomWsConnectionResult {
  isConnected: boolean;
  error: string | null;
}

/**
 * Shared Crypto.com WebSocket connection hook.
 *
 * All hook instances share a single module-level WebSocket connection.
 * The connection opens when the first consumer mounts and closes when the last unmounts.
 * Heartbeat, reconnection, and visibility handling are managed centrally.
 *
 * The `setup` callback is called when the WS is open. It should subscribe to channels
 * and return a cleanup function that unsubscribes.
 */
export const useCryptocomWsConnection = ({
  setup,
  onMessage,
  enabled = true,
  onError,
}: UseCryptocomWsConnectionProps): UseCryptocomWsConnectionResult => {
  const { isConnected: connected, error } = useSyncExternalStore(subscribeToStatus, getSnapshotStable);

  useEffect(() => {
    if (!enabled) return;

    registerVisibilityHandler();

    const id = subscribe({
      setup,
      onMessage,
      onError,
      cleanup: null,
    });

    return () => {
      unsubscribe(id);
    };
  }, [enabled, setup, onMessage, onError]);

  return {
    isConnected: enabled && connected,
    error: enabled ? error : null,
  };
};
